import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import {
  ClearRequestPayload,
  ClearResponse,
  ClearService,
  LegacyClearResponse,
} from '@shared/services/clear/clear.service';
import { catchError, switchMap, withLatestFrom } from 'rxjs';

import {
  ClearEnd,
  ClearError,
  ClearStart,
  Next,
  TrackProcessError,
  TrackProcessSideStep,
} from '../../actions';
import * as fromRootStore from '../../index';
import { selectDecedentInfoState } from '../../selectors/decedent-info/decedent-info.selectors';

@Injectable()
export class ClearEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private clearService: ClearService,
    private loggerService: LoggerService,
  ) {}

  clearStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClearStart),
      withLatestFrom(this.store$.pipe(select(selectDecedentInfoState))),
      switchMap(([, decedentInfoState]) => {
        return this.clearService
          .callExternalClientSearch({
            firstName: decedentInfoState.firstName,
            lastName: decedentInfoState.lastName,
            decedentId_private: decedentInfoState.decedentId_private,
            decedentPoid: decedentInfoState.decedentPoid,
            dateOfBirth: decedentInfoState.dateOfBirth,
          } as ClearRequestPayload)
          .pipe(
            switchMap((clearResponse: ClearResponse | LegacyClearResponse) => {
              let clearMessage;

              if (clearResponse.CNMKongToggle) {
                if ((clearResponse as ClearResponse).deceased) {
                  clearMessage = 'clear-verified_deceased';
                  this.loggerService.info(
                    {
                      message:
                        'Kong External client search returned deceased true, skipping document upload page',
                    },
                    LoggerCodes.DECEASED_TRUE,
                  );
                } else {
                  clearMessage = 'clear-verified_living';
                  this.loggerService.info(
                    {
                      message:
                        'Kong External client search returned deceased false, routing to document upload page',
                    },
                    LoggerCodes.DECEASED_FALSE,
                  );
                }

                return [
                  TrackProcessSideStep({
                    processType: 'coo-inheritance-flow',
                    processStep: `coo-inheritance-doc_upload`,
                    processSideStep: clearMessage,
                  }),
                  ClearEnd({ payload: clearResponse as ClearResponse }),
                ];
              } else {
                if ((clearResponse as LegacyClearResponse).isDeceased) {
                  clearMessage = 'clear-verified_deceased';
                  this.loggerService.info(
                    {
                      message:
                        'Legacy External client search returned deceased true, skipping document upload page',
                    },
                    LoggerCodes.DECEASED_TRUE,
                  );
                } else {
                  clearMessage = 'clear-verified_living';
                  this.loggerService.info(
                    {
                      message:
                        'Legacy External client search returned deceased false, routing to document upload page',
                    },
                    LoggerCodes.DECEASED_FALSE,
                  );
                }
                return [
                  TrackProcessSideStep({
                    processType: 'coo-inheritance-flow',
                    processStep: `coo-inheritance-doc_upload`,
                    processSideStep: clearMessage,
                  }),
                  ClearEnd({ payload: clearResponse as LegacyClearResponse }),
                ];
              }
            }),
            catchError((err) => {
              this.loggerService.info(
                {
                  message: `External client search unsuccessful/unavailable: ${err.message}, routing to document upload page`,
                },
                LoggerCodes.DECEASED_NOT_FOUND,
              );
              return [
                TrackProcessError({
                  processStep: `coo-inheritance-doc_upload`,
                  errorCode: 'clear-unverified',
                }),
                ClearError(),
              ];
            }),
          );
      }),
    ),
  );

  clearEnd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClearEnd),
      switchMap(() => {
        return [Next()];
      }),
    ),
  );

  clearError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClearError),
      switchMap(() => {
        return [Next()];
      }),
    ),
  );
}
